import React from 'react';
import { ReactComponent as CloseIcon } from 'app/assets/img/close_icon.svg';
import ModalContainer from 'app/components/ModalContainer';
import moment from 'moment';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { formatNumber } from 'utils/helper';
import type { Airport } from '../types';
import { OrderDetail, FlightType } from '../types';

interface FlightDetailModalProps {
  visible: boolean;
  onClose: () => void;
  order: OrderDetail;
  airports: Airport[];
}

const formatDuration = (departureTime: string, arrivalTime: string) => {
  const departure = moment(departureTime);
  const arrival = moment(arrivalTime);
  const duration = moment.duration(arrival.diff(departure));
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  if (hours && minutes) return `${hours}h${minutes}p`;
  if (hours) return `${hours}h`;
  return `${minutes}p`;
};

const getAirportDisplay = (code: string, airports: Airport[]) => {
  const airport = airports.find(a => a.code === code);
  if (airport) {
    return [code, airport.name, airport.terminal ? `T${airport.terminal}` : '']
      .filter(Boolean)
      .join(' ');
  }
  return code;
};

export const FlightDetailModal: React.FC<FlightDetailModalProps> = ({
  visible,
  onClose,
  order,
  airports,
}) => {
  const translations = useSelector(selectTranslations);

  const firstItem = order.orderItems[0];
  if (!firstItem) return null;

  const { flight } = firstItem;
  const flightNumber = `${flight.carrier || '9G'}${flight.flightNumber}`;

  // Get return flight if round trip
  const returnFlight =
    order.flightType === FlightType.ROUND_TRIP && order.orderItems[1]
      ? order.orderItems[1].flight
      : null;

  const getCabinClassLabel = (cabinClass?: string) => {
    if (!cabinClass) return 'Economy';
    if (cabinClass.toLowerCase().includes('business')) return 'Business';
    return 'Economy';
  };

  const renderFareRule = (rule: any) => {
    let status = '';

    switch (rule.type) {
      case 0:
        status = rule.value || (translations?.['b2b.txt.fareRuleAllowed'] || 'Cho phép');
        break;
      case 1:
        status = rule.value || (translations?.['b2b.txt.fareRuleFee'] || 'Tính phí');
        break;
      case 2:
        status = translations?.['b2b.txt.fareRuleNo'] || 'Không';
        break;
    }

    return `${rule.fareRuleName}: ${status}`;
  };

  return (
    <ModalContainer
      isShow={visible}
      onCloseDialog={onClose}
      maskClosable={false}
      modalPadding="16px 24px 32px"
      modalHeight="initial"
      styleSlots={{
        iconWrapperStyle: {
          margin: '0 16px 0 0',
        },
        modalContentStyle: {
          maxWidth: 700,
          width: '96vw',
          maxHeight: '96vh',
        },
        modalBodyStyle: {
          height: 'fit-content',
        },
      }}
      modalProps={{ destroyOnClose: true }}
      CloseIcon={<CloseIcon width={12} height={12} />}
    >
      <TitleModel>
        {translations?.['b2b.txt.flightDetail'] || 'Chi tiết chuyến bay'}
      </TitleModel>
      <Line />

      {/* Chiều đi */}
      <FlightLegCard>
        <Header>
          <Title>{translations?.['b2b.txt.departureLeg'] || 'Chiều đi'}</Title>
          <Price>{formatNumber(firstItem.combo.salePrice || 0, '.')}đ</Price>
        </Header>
        <Content>
          <Timeline>
            <span className="dot" />
            <span className="line" />
            <span className="dot" />
          </Timeline>
          <Info>
            <Row>
              <Time>{moment(flight.departureTime).format('HH:mm')}</Time>
              <Location>
                {getAirportDisplay(flight.departure, airports)}
              </Location>
            </Row>
            <Meta>
              <Duration>
                {formatDuration(flight.departureTime, flight.arrivalTime)}
              </Duration>
              <Aircraft>{flightNumber}</Aircraft>
            </Meta>
            <Row>
              <Time>{moment(flight.arrivalTime).format('HH:mm')}</Time>
              <Location>{getAirportDisplay(flight.arrival, airports)}</Location>
            </Row>
          </Info>
        </Content>

        {/* Chi tiết chuyến bay */}
        <FlightDetails>
          <DetailRow>
            <DetailLabel>{translations?.['b2b.txt.flightNumber'] || 'Số hiệu chuyến bay'}:</DetailLabel>
            <DetailValue>{flightNumber}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{translations?.['b2b.txt.cabinClass'] || 'Hạng'}:</DetailLabel>
            <DetailValue>{getCabinClassLabel(flight.cabinClass)}</DetailValue>
          </DetailRow>
        </FlightDetails>
      </FlightLegCard>

      {/* Chiều về */}
      {returnFlight && (
        <FlightLegCard>
          <Header>
            <Title>{translations?.['b2b.txt.returnLeg'] || 'Chiều về'}</Title>
            <Price>
              {formatNumber(order.orderItems[1].combo.salePrice || 0, '.')}đ
            </Price>
          </Header>
          <Content>
            <Timeline>
              <span className="dot" />
              <span className="line" />
              <span className="dot" />
            </Timeline>
            <Info>
              <Row>
                <Time>
                  {moment(returnFlight.departureTime).format('HH:mm')}
                </Time>
                <Location>
                  {getAirportDisplay(returnFlight.departure, airports)}
                </Location>
              </Row>
              <Meta>
                <Duration>
                  {formatDuration(
                    returnFlight.departureTime,
                    returnFlight.arrivalTime,
                  )}
                </Duration>
                <Aircraft>
                  {`${returnFlight.carrier || '9G'}${
                    returnFlight.flightNumber
                  }`}
                </Aircraft>
              </Meta>
              <Row>
                <Time>{moment(returnFlight.arrivalTime).format('HH:mm')}</Time>
                <Location>
                  {getAirportDisplay(returnFlight.arrival, airports)}
                </Location>
              </Row>
            </Info>
          </Content>

          {/* Chi tiết chuyến bay */}
          <FlightDetails>
            <DetailRow>
              <DetailLabel>{translations?.['b2b.txt.flightNumber'] || 'Số hiệu chuyến bay'}:</DetailLabel>
              <DetailValue>
                {`${returnFlight.carrier || '9G'}${returnFlight.flightNumber}`}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>{translations?.['b2b.txt.cabinClass'] || 'Hạng'}:</DetailLabel>
              <DetailValue>
                {getCabinClassLabel(returnFlight.cabinClass)}
              </DetailValue>
            </DetailRow>
          </FlightDetails>
        </FlightLegCard>
      )}

      {/* Chính sách dịch vụ */}
      {order.fareRules && order.fareRules.length > 0 && (
        <PolicySection>
          <PolicyTitle>{translations?.['b2b.txt.servicePolicy'] || 'Chính sách dịch vụ'}</PolicyTitle>
          <PolicyList>
            {order.fareRules.map((rule, index) => (
              <PolicyItem key={index}>{renderFareRule(rule)}</PolicyItem>
            ))}
          </PolicyList>
        </PolicySection>
      )}
    </ModalContainer>
  );
};

const TitleModel = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const Line = styled.div`
  height: 1px;
  background-color: #e5e5e5;
  margin: 16px -24px;
`;

const FlightLegCard = styled.div`
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  font-size: 14px;
  border: 1px solid #e5e5e5;
  margin-bottom: 16px;
`;

const Header = styled.div`
  background: #f5f5f5;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-weight: 500;
`;

const Price = styled.div`
  font-weight: 600;
`;

const Content = styled.div`
  display: flex;
  padding: 14px;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 12px;
  padding-top: 6px;
  padding-bottom: 6px;

  .dot {
    width: 8px;
    height: 8px;
    background: #262626;
    border-radius: 50%;
  }

  .line {
    flex: 1;
    width: 2px;
    background: #ddd;
    margin: 0px 0;
  }
`;

const Info = styled.div`
  flex: 1;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
`;

const Time = styled.div`
  min-width: 48px;
  font-weight: 600;
`;

const Location = styled.div``;

const Meta = styled.div`
  margin: 4px 0 8px 56px;
  font-size: 12px;
  display: flex;
  gap: 12px;
`;

const Duration = styled.div`
  color: #8d8d8d;
`;

const Aircraft = styled.div`
  color: var(--color-9);
`;

const FlightDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 14px 14px 14px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
  padding-top: 12px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 12px;
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: #666;
  min-width: 140px;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: #333;
  font-weight: 500;
`;

const PolicySection = styled.div`
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  font-size: 14px;
  border: 1px solid #e5e5e5;
  margin-bottom: 16px;
`;

const PolicyTitle = styled.div`
  background: #f5f5f5;
  padding: 10px 14px;
  font-weight: 500;
`;

const PolicyList = styled.ul`
  list-style: none;
  padding: 14px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PolicyItem = styled.li`
  font-size: 13px;
  color: #666;
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '✓';
    color: #52c41a;
    margin-right: 8px;
    font-weight: bold;
  }
`;
